import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icons } from '@/components/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DASHBOARD_QUERY_KEY } from '../hooks/use-get-dashboard';
import { useUpdateDashboard } from '../hooks/use-update-dashboard';
import type {
  DashBoard,
  DashboardSettings,
  WorkEngagementDashboard,
} from '../types';
import { Switch } from '@/components/ui/switch';
import { useLocalStorage } from 'usehooks-ts';

export function UpdateDashboardModal({
  closeFunction,
  currentDashboard,
}: {
  closeFunction: () => void;
  currentDashboard: DashBoard | WorkEngagementDashboard | undefined;
}) {
  const [dashboardNameUpdate, setDashboardNameUpdate] = useState('');
  const [hideKeyIndexes, setHideKeyIndexes] = useState(false);
  const [dashboardSettings, setDashboardSettings] =
    useLocalStorage<DashboardSettings>('dashboardSettings', {
      hiddenItems: {},
    });

  const { mutateAsync: mutateDashboard, isPending: isUpdatingDashboard } =
    useUpdateDashboard();
  const queryClient = useQueryClient();
  const clearAndClose = () => {
    setDashboardNameUpdate('');
    closeFunction();
  };

  useEffect(() => {
    if (currentDashboard) {
      const isHidden =
        dashboardSettings.hiddenItems['key-indexes']?.includes(
          currentDashboard.key
        ) ?? false;
      setHideKeyIndexes(isHidden);
    }
  }, [currentDashboard, dashboardSettings]);

  const updateDashboard = () => {
    if (!currentDashboard) return;

    setDashboardSettings((prev) => {
      const current = prev.hiddenItems['key-indexes'] ?? [];

      const next = hideKeyIndexes
        ? current.includes(currentDashboard.key)
          ? current
          : [...current, currentDashboard.key]
        : current.filter((id) => id !== currentDashboard.key);

      return {
        ...prev,
        hiddenItems: {
          ...prev.hiddenItems,
          ['key-indexes']: next,
        },
      };
    });

    if (dashboardNameUpdate.trim().length === 0) {
      toast.success('Dashboard sparades');
      clearAndClose();
      return;
    }

    currentDashboard.name = dashboardNameUpdate;

    toast.promise(mutateDashboard(currentDashboard), {
      loading: 'Sparar...',
      success: () => {
        queryClient.invalidateQueries({
          queryKey: [DASHBOARD_QUERY_KEY],
        });
        clearAndClose();
        return 'Dashboard sparades';
      },
      error: 'Misslyckades med att spara dashboard',
    });
  };

  return (
    <Dialog defaultOpen onOpenChange={clearAndClose}>
      <DialogContent size='sm' className='p-0'>
        <DialogHeader className='border-b-0'>
          <DialogTitle>Redigera {currentDashboard?.name}</DialogTitle>
          <Button variant='outline' onClick={clearAndClose} size={'icon-sm'}>
            <Icons.close className='size-4 shrink-0' />
          </Button>
          <DialogDescription className='sr-only'>
            Redigera {currentDashboard?.name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className='grid gap-4 p-4'>
            <div className='grid gap-2'>
              <Label htmlFor='dashboard-name'>Namn på dashboard</Label>
              <Input
                id='dashboard-name'
                placeholder='Namn på dashboard'
                defaultValue={currentDashboard?.name}
                onChange={(e) => setDashboardNameUpdate(e.target.value)}
              />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='dashboard-hide-keyindexes'>
                Dölj nyckeltal på dashboard
              </Label>
              <Switch
                id='dashboard-hide-keyindexes'
                checked={hideKeyIndexes}
                onCheckedChange={(checked) => setHideKeyIndexes(checked)}
              />
            </div>
          </div>
          <DialogFooter className='border-t-0'>
            <Button
              disabled={isUpdatingDashboard}
              type='submit'
              onClick={updateDashboard}
            >
              Spara
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
